import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { asyncConnect } from 'redux-connect';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Grid, Row, Dropdown, MenuItem } from 'react-bootstrap';

import Loader from 'components/Loader/Loader';
import ApplicantView from 'components/ClientATS/ApplicantView';
import ApplicantList from 'components/ClientATS/ApplicantList';
import NoApplicantsEmptyState from 'components/ClientATS/NoApplicantsEmptyState';
import EditJobModal from 'components/ClientATS/EditJobModal';
import PublicQuestionsModal from 'components/ClientATS/PublicQuestionsModal';
import Modal from 'components/Modal/Modal';
import PaymentModal from 'components/Pitch/PaymentModal';
import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';

import {
  loadBrief,
  loadPitches,
  refreshPitches,
  markPitchAsRead,
  markPitchThreadAsRead,
  declinePitch,
  shortlistPitch,
  loadPitchMessages,
  sendPitchMessage,
  endHire,
  resetPitch,
  acceptPitch,
  loadPublicQuestions,
  updateBrief,
} from 'redux/modules/clientATS';
import { getBrowseURL } from 'helpers/HelperFunctions';

import { closeBrief, reopenBrief } from 'redux/modules/projects';

import styles from './JobDetails.module.scss';

const JobDetails = ({ match }) => {
  const [activeTab, setActiveTab] = useState('applied');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [showJobMenu, setShowJobMenu] = useState(false);
  const [showHireModal, setShowHireModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingBrief, setSavingBrief] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showCloseJobModal, setShowCloseJobModal] = useState(false);
  const [closingJob, setClosingJob] = useState(false);
  const [showReopenJobModal, setShowReopenJobModal] = useState(false);
  const [reopeningJob, setReopeningJob] = useState(false);
  const [aiRecruiterEnabled, setAiRecruiterEnabled] = useState(null);
  const [isBoosted, setIsBoosted] = useState(false);
  const [isPrivate, setIsPrivate] = useState(null);
  const tabRefs = useRef({});
  const dispatch = useDispatch();
  const history = useHistory();

  const auth = useSelector((state) => state.auth);
  const {
    brief,
    loading,
    pitches,
    loadingPitches,
    pitchesLoaded,
    decliningPitch,
    shortlistingPitch,
    endingHire,
    resettingPitch,
    acceptingPitch,
    pitchMessages,
    pitchMessagesMeta,
    loadingPitchMessages,
    markingThreadAsRead,
    markedThreads,
    publicQuestions,
    publicQuestionsLoaded,
  } = useSelector((state) => state.clientATS);

  // Set isDesktop on mount and handle window resize
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    // Set initial value
    checkIsDesktop();

    // Add resize listener
    window.addEventListener('resize', checkIsDesktop);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIsDesktop);
    };
  }, []);

  const encodedId = useMemo(() => {
    return match.params.id ? match.params.id.split('-')[0] : null;
  }, [match.params.id]);

  // Load brief data on component mount or when encodedId changes
  useEffect(() => {
    if (encodedId && !brief && !loading) {
      dispatch(loadBrief(encodedId));
    }
  }, [encodedId, brief, loading, dispatch]);

  // Load pitches when brief is loaded
  useEffect(() => {
    if (
      brief &&
      brief.id &&
      auth?.user?.id === brief.user.id &&
      !pitchesLoaded &&
      !loadingPitches
    ) {
      dispatch(loadPitches(brief.id));
    }
  }, [brief, auth, pitchesLoaded, loadingPitches, dispatch]);

  // Reload pitches when returning from payment page (check for URL params)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('paymentSuccess');

    if (paymentSuccess && brief?.id && pitchesLoaded) {
      // Reload pitches to get updated state after payment
      dispatch(loadPitches(brief.id));

      // Clean up URL params
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [brief, pitchesLoaded, dispatch]);

  // Default to 'invited' tab if there are no applicants in the 'applied' tab (only on initial load)
  const hasCheckedInitialTab = useRef(false);
  useEffect(() => {
    if (pitchesLoaded && !hasCheckedInitialTab.current) {
      hasCheckedInitialTab.current = true;
      // Check if there are any pitches with 'applied' stage
      const hasAppliedPitches = pitches.some((pitch) => {
        return (
          pitch.approved_at &&
          !pitch.accepted_at &&
          !pitch.completed_at &&
          !pitch.declined_at &&
          !pitch.liked_at
        );
      });
      if (!hasAppliedPitches) {
        setActiveTab('invited');
      }
    }
  }, [pitchesLoaded, pitches]);

  // Redirect if user is not the owner of the job
  useEffect(() => {
    if (brief && auth.loaded) {
      if (!auth.user) {
        history.replace(brief.links.main_relative);
      } else {
        // Check if the current user is the owner of the brief
        const isOwner = brief.user && brief.user.id === auth.user.id;

        if (!isOwner) {
          // Redirect to the project page without /ats
          history.replace(brief.links.main_relative);
        }
      }
    }
  }, [brief, auth.user, history]);

  // Load public questions when brief is loaded
  useEffect(() => {
    if (brief && brief.id && !publicQuestionsLoaded) {
      dispatch(loadPublicQuestions(brief.id));
    }
  }, [brief, publicQuestionsLoaded, dispatch]);

  useEffect(() => {
    if (brief) {
      setIsBoosted(!!brief?.boosted_expires_at);
    }
  }, [brief]);

  // Helper function to transform user data to applicant format
  const transformUserToApplicant = (
    user,
    stage,
    timeAgo,
    isUnread = false,
    pitchId = null,
    hasUnreadMessages = false
  ) => {
    // Handle location
    let locationText = 'Location not specified';
    if (user.location) {
      if (typeof user.location === 'string') {
        locationText = user.location;
      } else if (user.location.text) {
        locationText = user.location.text;
      }
    }

    // Get roles
    const roles = user.featured_roles || user.top_roles || [];

    return {
      id: pitchId || user.id,
      userId: user.id,
      name: user.displayname || user.username || 'Unknown',
      avatars: user.avatars,
      location: locationText,
      roles: roles.slice(0, 3),
      skills: user.skills,
      stage: stage,
      timeAgo: timeAgo,
      isUnread: isUnread,
      hasUnreadMessages: hasUnreadMessages,
      user: user, // Include full user object for normalized access
    };
  };

  // Transform invited users from brief
  const invitedApplicants = useMemo(() => {
    const invites = [];
    if (brief && brief.invites && brief.invites.invites) {
      // Get set of user IDs who have already applied (have pitches)
      const appliedUserIds = new Set(
        pitches.map((pitch) => pitch.sender?.id).filter(Boolean)
      );

      brief.invites.invites.forEach((invite) => {
        // Only include invited users who haven't applied yet
        if (!appliedUserIds.has(invite.user?.id)) {
          invites.push(
            transformUserToApplicant(
              invite.user,
              'invited',
              invite.created_at,
              false,
              `invite-${invite.id}`
            )
          );
        }
      });
    }

    return invites;
  }, [brief, pitches]);

  // Transform pitches data to applicant format
  const pitchApplicants = useMemo(() => {
    return pitches.map((pitch) => {
      const sender = pitch.sender || {};

      // Determine stage based on pitch status
      // Priority order: hired > declined > shortlisted > applied
      let stage = 'applied';
      if (pitch.completed_at || pitch.accepted_at) {
        stage = 'hired';
      } else if (pitch.declined_at) {
        stage = 'declined';
      } else if (pitch.liked_at) {
        stage = 'shortlisted';
      } else if (pitch.approved_at) {
        stage = 'applied';
      }

      return transformUserToApplicant(
        sender,
        stage,
        pitch.timestamp,
        !pitch.receiver_viewed,
        pitch.id,
        pitch.has_unread_messages
      );
    });
  }, [pitches]);

  // Filter applicants by active tab
  const filteredApplicants = useMemo(() => {
    if (activeTab === 'invited') {
      return invitedApplicants;
    }
    return pitchApplicants.filter((applicant) => applicant.stage === activeTab);
  }, [activeTab, invitedApplicants, pitchApplicants]);

  // Auto-select first applicant when tab changes or applicants load (desktop only)
  useEffect(() => {
    // Don't auto-select or clear selection while hire modal is open
    // This prevents clearing selectedApplicant when the hired user moves to a different tab
    if (showHireModal) {
      return;
    }

    // Only auto-select on desktop (screen width > 768px)
    if (filteredApplicants.length > 0) {
      // Check if currently selected applicant is still in the filtered list
      const isSelectedInList =
        selectedApplicant &&
        filteredApplicants.some((app) => app.id === selectedApplicant.id);

      // If no selection or selected applicant is not in current tab, select first one
      if (!isSelectedInList) {
        if (isDesktop) {
          setSelectedApplicant(filteredApplicants[0]);
          // Mark as read if it's unread
          if (filteredApplicants[0].isUnread) {
            dispatch(markPitchAsRead(filteredApplicants[0].id));
          }
        } else {
          // On mobile, clear selection when selected applicant is not in current tab
          setSelectedApplicant(null);
        }
      }
    } else {
      // Clear selection if no applicants in current tab
      setSelectedApplicant(null);
    }
  }, [filteredApplicants, activeTab, showHireModal]);

  // Handle mobile detail view visibility when tab changes
  useEffect(() => {
    if (!isDesktop) {
      // On mobile, return to list view when tab changes
      setShowMobileDetail(false);

      // Scroll active tab into view on mobile
      if (tabRefs.current[activeTab]) {
        tabRefs.current[activeTab].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeTab, isDesktop]);

  // Calculate tab counts
  const tabCounts = useMemo(() => {
    return {
      invited: invitedApplicants.length,
      applied: pitchApplicants.filter((a) => a.stage === 'applied').length,
      declined: pitchApplicants.filter((a) => a.stage === 'declined').length,
      shortlisted: pitchApplicants.filter((a) => a.stage === 'shortlisted')
        .length,
      hired: pitchApplicants.filter((a) => a.stage === 'hired').length,
    };
  }, [invitedApplicants, pitchApplicants]);

  const getTabCount = (tabId) => {
    // Don't show counts while loading
    if (loadingPitches) {
      return '';
    }
    return tabCounts[tabId] || 0;
  };

  // Find the full pitch data for the selected applicant - recomputes when selection changes
  // MUST be before early return to follow React hooks rules
  const selectedPitch = useMemo(() => {
    if (!selectedApplicant) return null;

    // For invited applicants, we don't need to create pitch data
    // ApplicantView will handle them directly via the applicant prop
    const isInvited = selectedApplicant.stage === 'invited';
    if (isInvited) {
      return null;
    }

    // Find the actual pitch
    return pitches.find((p) => p.id === selectedApplicant.id) || null;
  }, [selectedApplicant, pitches]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Create browse URL with job filters
  const browseURL = useMemo(() => {
    if (!brief) return '/browse';
    return getBrowseURL({
      role: brief.role,
      skill: brief.skill,
      ...(brief.remote ? {} : { location: brief.location }),
    });
  }, [brief]);

  // Early returns after all hooks
  if (!auth.loaded || loading || !brief) {
    return <Loader loading />;
  }

  const tabs = [
    { id: 'invited', label: 'Invited' },
    { id: 'applied', label: 'Applied' },
    { id: 'declined', label: 'Declined' },
    { id: 'shortlisted', label: 'Shortlisted' },
    { id: 'hired', label: 'Hired' },
  ];

  const briefStatus = brief?.status;
  const briefTitle = brief?.text;
  const postedDate = formatDate(brief?.timestamp);

  // Helper function to select next applicant after an action
  const selectNextApplicant = (currentIndex) => {
    // On mobile, return to list view instead of selecting next applicant
    if (!isDesktop) {
      setShowMobileDetail(false);
      setSelectedApplicant(null);
      return;
    }

    // Desktop behavior: select next applicant
    if (filteredApplicants.length > 1) {
      // If there's a next applicant, select it
      if (currentIndex < filteredApplicants.length - 1) {
        setSelectedApplicant(filteredApplicants[currentIndex + 1]);
        // Mark as read if it's unread
        if (filteredApplicants[currentIndex + 1].isUnread) {
          dispatch(markPitchAsRead(filteredApplicants[currentIndex + 1].id));
        }
      } else if (currentIndex > 0) {
        // Otherwise select the previous one
        setSelectedApplicant(filteredApplicants[currentIndex - 1]);
        // Mark as read if it's unread
        if (filteredApplicants[currentIndex - 1].isUnread) {
          dispatch(markPitchAsRead(filteredApplicants[currentIndex - 1].id));
        }
      } else {
        // No other applicants in this tab
        setSelectedApplicant(null);
      }
    } else {
      setSelectedApplicant(null);
    }
  };

  // Action handlers
  const handleDecline = () => {
    if (!selectedApplicant || !brief) return;

    // Find current applicant index in filtered list
    const currentIndex = filteredApplicants.findIndex(
      (app) => app.id === selectedApplicant.id
    );

    // Dispatch decline action
    dispatch(declinePitch(brief.id, selectedApplicant.id)).then(() => {
      selectNextApplicant(currentIndex);
    });
  };

  const handleShortlist = () => {
    if (!selectedApplicant || !brief) return;

    // Find current applicant index in filtered list
    const currentIndex = filteredApplicants.findIndex(
      (app) => app.id === selectedApplicant.id
    );

    // Dispatch shortlist action
    dispatch(shortlistPitch(brief.id, selectedApplicant.id)).then(() => {
      selectNextApplicant(currentIndex);
    });
  };

  const handleHire = () => {
    if (!selectedApplicant || !brief) return;
    setShowHireModal(true);
  };

  const handleAcceptPitchWithPayment = (totalToPay) => {
    if (!selectedApplicant || !brief || !selectedPitch) return;

    // Convert totalToPay to cents (totalToPay is in dollars/pounds, API expects cents/pence)
    const totalToPayInCents = totalToPay * 100;

    // Redirect to payment page, similar to the existing flow
    // The payment page will handle the actual payment processing
    history.push(
      `/manage/briefs/${brief.id}/pay/${
        selectedPitch.id
      }?amount=${totalToPayInCents}&isHire=true&returnTo=${encodeURIComponent(
        window.location.pathname
      )}`
    );
  };

  // eslint-disable-next-line no-unused-vars
  const handleAcceptPitchWithoutPayment = () => {
    if (!selectedApplicant || !brief) return;

    // Find current applicant index in filtered list
    const currentIndex = filteredApplicants.findIndex(
      (app) => app.id === selectedApplicant.id
    );

    // Dispatch accept pitch action without payment (skip payment flow)
    dispatch(acceptPitch(brief.id, selectedApplicant.id)).then(() => {
      // Don't close modal here - let the MarkedAsHired step show first
      // User will close it manually or via "Hire Others" button
      selectNextApplicant(currentIndex);
    });
  };

  const handleCloseHireModal = () => {
    setShowHireModal(false);
  };

  const handleEndHire = () => {
    if (!selectedApplicant || !brief) return;

    // Don't allow actions on dummy data
    if (selectedApplicant.isDummyData) {
      console.warn('Cannot end hire for dummy applicant');
      return;
    }

    // Dispatch end hire action and silently refresh pitches to get the review URL code
    dispatch(endHire(brief.id, selectedApplicant.id)).then(() => {
      // Silently refresh pitches to get fresh data with the review URL code
      // This won't trigger the loading spinner or tab count updates
      dispatch(refreshPitches(brief.id));
    });
  };

  const handleReset = () => {
    if (!selectedApplicant || !brief) return;

    // Dispatch reset pitch action
    dispatch(resetPitch(brief.id, selectedApplicant.id));
  };

  const handleLoadMessages = (username, pitchId, markAsRead = false) => {
    dispatch(loadPitchMessages(username, pitchId, markAsRead));
  };

  const handleLoadMoreMessages = (pitchId) => {
    if (!selectedPitch) return;

    const username = selectedPitch.sender.username;
    const currentMeta = pitchMessagesMeta[pitchId] || { pagesLoaded: 0 };
    const nextPage = currentMeta.pagesLoaded + 1;

    dispatch(loadPitchMessages(username, pitchId, false, nextPage));
  };

  const handleMarkThreadAsRead = (username, pitchId) => {
    dispatch(markPitchThreadAsRead(username, pitchId));
  };

  const handleSendMessage = (message) => {
    if (!selectedApplicant || !selectedPitch) {
      console.error('No applicant or pitch selected');
      return;
    }

    const recipient = selectedPitch.sender;
    const pitchId = selectedPitch.id;
    const currentUserId = auth.user?.id;

    dispatch(sendPitchMessage(recipient, message, pitchId, currentUserId));
  };

  const handleEditJobClick = (e) => {
    e.preventDefault();
    setShowJobMenu(false);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleSaveJob = (payload) => {
    setSavingBrief(true);

    dispatch(updateBrief(encodedId, payload))
      .then(() => {
        setSavingBrief(false);
        setShowEditModal(false);
      })
      .catch((error) => {
        setSavingBrief(false);
        console.error('Error updating brief:', error);
        // You could add error handling/notification here
      });
  };

  const handleToggleAIRecruiter = () => {
    const currentValue = aiRecruiterEnabled ?? brief?.ai_recruiter;
    const newValue = !currentValue;

    // Optimistically update local state for instant UI feedback
    setAiRecruiterEnabled(newValue);

    // Dispatch API call in background
    dispatch(updateBrief(encodedId, { ai_recruiter: newValue })).catch(
      (error) => {
        console.error('Error toggling AI recruiter:', error);
        // Revert on error
        setAiRecruiterEnabled(currentValue);
      }
    );
  };

  const handleTogglePrivate = () => {
    const currentValue = isPrivate ?? brief?.private;
    const newValue = !currentValue;

    // Optimistically update local state for instant UI feedback
    setIsPrivate(newValue);

    // Dispatch API call in background
    dispatch(updateBrief(encodedId, { private: newValue })).catch((error) => {
      console.error('Error toggling private status:', error);
      // Revert on error
      setIsPrivate(currentValue);
    });
  };

  const handleCloseJobClick = () => {
    setShowJobMenu(false);
    setShowCloseJobModal(true);
  };

  const handleCloseJobModalCancel = () => {
    setShowCloseJobModal(false);
  };

  const handleCloseJobConfirm = () => {
    if (!brief || closingJob) return;

    setClosingJob(true);
    dispatch(closeBrief(brief.id))
      .then(() => {
        // Reload the brief to get updated status
        dispatch(loadBrief(encodedId));
        setClosingJob(false);
        setShowCloseJobModal(false);
      })
      .catch((error) => {
        console.error('Failed to close job:', error);
        setClosingJob(false);
      });
  };

  const handleReopenJobClick = () => {
    setShowJobMenu(false);
    setShowReopenJobModal(true);
  };

  const handleReopenJobModalCancel = () => {
    setShowReopenJobModal(false);
  };

  const handleReopenJobConfirm = () => {
    if (!brief || reopeningJob) return;

    setReopeningJob(true);
    dispatch(reopenBrief(brief.id))
      .then(() => {
        // Reload the brief to get updated status
        dispatch(loadBrief(encodedId));
        setReopeningJob(false);
        setShowReopenJobModal(false);
      })
      .catch((error) => {
        console.error('Failed to re-open job:', error);
        setReopeningJob(false);
      });
  };

  const boostDaysRemaining = brief?.boosted_expires_at
    ? Math.ceil(
        (new Date(brief.boosted_expires_at).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  return (
    <Grid className={styles.wrapper}>
      <Row>
        <Col lg={12}>
          <div className={styles.header}>
            <div>
              <div className={styles.backLink}>
                <a className={styles.backButton} href="/manage/briefs">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m12 19-7-7 7-7"></path>
                    <path d="M19 12H5"></path>
                  </svg>
                  <span>Back to Dashboard</span>
                </a>
              </div>

              <div className={styles.titleSection}>
                <div className={styles.titleWrapper}>
                  <div className={styles.statusBadgeWrapper}>
                    <span
                      className={`${styles.statusBadge} ${
                        styles.statusBadgeMobile
                      } ${briefStatus === 'active' ? styles.statusActive : ''}`}
                    >
                      {briefStatus}
                    </span>
                    {(isPrivate ?? brief?.private) && (
                      <span
                        className={`${styles.statusBadge} ${styles.statusBadgeMobile} ${styles.statusPrivate}`}
                      >
                        Private
                      </span>
                    )}
                  </div>
                  <div className={styles.titleContent}>
                    <h3 className={styles.title}>{briefTitle}</h3>
                    <span
                      className={`${styles.statusBadge} ${
                        styles.statusBadgeDesktop
                      } ${briefStatus === 'active' ? styles.statusActive : ''}`}
                    >
                      {briefStatus}
                    </span>
                    {(isPrivate ?? brief?.private) && (
                      <span
                        className={`${styles.statusBadge} ${styles.statusBadgeDesktop} ${styles.statusPrivate}`}
                      >
                        Private
                      </span>
                    )}
                  </div>
                  <p className={styles.postedDate}>Posted {postedDate}</p>
                </div>
                <Dropdown
                  id="job-menu-dropdown"
                  pullRight
                  open={showJobMenu}
                  onToggle={(isOpen) => setShowJobMenu(isOpen)}
                >
                  <Dropdown.Toggle noCaret className={styles.jobMenuButton}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="19" cy="12" r="1" />
                      <circle cx="5" cy="12" r="1" />
                    </svg>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={styles.jobMenuDropdown}>
                    <MenuItem onClick={handleEditJobClick}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: '8px' }}
                      >
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                      Edit Job
                    </MenuItem>
                    {brief?.status === 'active' ? (
                      <MenuItem onClick={handleCloseJobClick}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: '8px' }}
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="m15 9-6 6" />
                          <path d="m9 9 6 6" />
                        </svg>
                        Close Job
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={handleReopenJobClick}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: '8px' }}
                        >
                          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                          <path d="M21 3v5h-5" />
                        </svg>
                        Re-open Job
                      </MenuItem>
                    )}
                    {isPrivate ?? brief?.private ? (
                      <MenuItem onClick={handleTogglePrivate}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: '8px' }}
                        >
                          <rect
                            width="18"
                            height="11"
                            x="3"
                            y="11"
                            rx="2"
                            ry="2"
                          />
                          <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                        </svg>
                        Make Public
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={handleTogglePrivate}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: '8px' }}
                        >
                          <rect
                            width="18"
                            height="11"
                            x="3"
                            y="11"
                            rx="2"
                            ry="2"
                          />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        Make Private
                      </MenuItem>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            {/* Action Cards Section */}
            <div className={styles.actionCardsSection}>
              <div className={styles.actionCardsGrid}>
                {/* Public Questions Card */}
                <button
                  className={styles.actionCard}
                  onClick={() => setShowQuestionsModal(true)}
                >
                  <div className={styles.actionCardBadge}>
                    {publicQuestions?.filter(
                      (q) => !q.answer || q.answer.trim() === ''
                    ).length || 0}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`${styles.actionCardIcon} ${styles.questionsActionCardIcon}`}
                  >
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
                  </svg>
                  <div className={styles.actionCardContent}>
                    <div className={styles.actionCardTitle}>Questions</div>
                    <div className={styles.actionCardSubtitle}>
                      {publicQuestions?.filter(
                        (q) => !q.answer || q.answer.trim() === ''
                      ).length || 0}{' '}
                      pending response
                      {(publicQuestions?.filter(
                        (q) => !q.answer || q.answer.trim() === ''
                      ).length || 0) !== 1
                        ? 's'
                        : ''}
                    </div>
                  </div>
                </button>

                {/* Boosted Card */}
                <a
                  className={styles.actionCard}
                  href="https://www.twine.net/enterprise"
                  target="_blank"
                  rel="noreferrer"
                >
                  <div
                    className={`${styles.actionCardBadge} ${
                      isBoosted ?? brief?.boosted_expires_at
                        ? styles.actionCardBadgeActive
                        : styles.actionCardBadgeInactive
                    }`}
                  >
                    {isBoosted ? 'On' : 'Off'}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`${styles.actionCardIcon} ${
                      isBoosted ?? brief?.boosted_expires_at
                        ? styles.boostedActionCardIconActive
                        : styles.boostedActionCardIconInactive
                    }`}
                  >
                    <path d="M16 7h6v6" />
                    <path d="m22 7-8.5 8.5-5-5L2 17" />
                  </svg>

                  <div className={styles.actionCardContent}>
                    <div className={styles.actionCardTitle}>Boost Job</div>
                    <div
                      className={`${styles.actionCardSubtitle} ${
                        isBoosted ? styles.actionCardSubtitleActive : ''
                      }`}
                    >
                      {isBoosted ?? brief?.boosted_expires_at
                        ? `Active • ${boostDaysRemaining} days left`
                        : 'Increase visibility'}
                    </div>
                  </div>
                </a>

                {/* AI Recruiter Card */}
                <button
                  className={styles.actionCard}
                  onClick={handleToggleAIRecruiter}
                >
                  <div
                    className={`${styles.actionCardBadge} ${
                      aiRecruiterEnabled ?? brief?.ai_recruiter
                        ? styles.actionCardBadgeActive
                        : styles.actionCardBadgeInactive
                    }`}
                  >
                    {aiRecruiterEnabled ?? brief?.ai_recruiter ? 'On' : 'Off'}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`${styles.actionCardIcon} ${
                      aiRecruiterEnabled ?? brief?.ai_recruiter
                        ? styles.recruiterActionCardIconActive
                        : styles.recruiterActionCardIconInactive
                    }`}
                  >
                    <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                    <path d="M20 2v4" />
                    <path d="M22 4h-4" />
                    <circle cx="4" cy="20" r="2" />
                  </svg>
                  <div className={styles.actionCardContent}>
                    <div className={styles.actionCardTitle}>AI Recruiter</div>
                    <div className={styles.actionCardSubtitle}>
                      {aiRecruiterEnabled ?? brief?.ai_recruiter
                        ? 'Auto-screening active'
                        : 'Enable automation'}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className={styles.tabsContainer}>
              <div className="container">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    ref={(el) => (tabRefs.current[tab.id] = el)}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSelectedApplicant(null);
                      setShowMobileDetail(false);
                    }}
                    className={`${styles.tab} ${
                      activeTab === tab.id ? styles.tabActive : ''
                    }`}
                  >
                    {tab.label}
                    {loadingPitches ? '' : ` (${getTabCount(tab.id)})`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row className={styles.content}>
        {/* Show empty state for no applicants */}
        {filteredApplicants.length === 0 && !loadingPitches ? (
          <Col xs={12}>
            <NoApplicantsEmptyState tab={activeTab} browseURL={browseURL} />
          </Col>
        ) : (
          <>
            <Col
              md={4}
              xs={12}
              className={`${styles.applicantColumn} ${
                showMobileDetail ? styles.hiddenMobile : styles.showMobile
              }`}
            >
              {loadingPitches ? (
                <Loader loading />
              ) : (
                <ApplicantList
                  applicants={filteredApplicants}
                  selectedApplicantId={selectedApplicant?.id}
                  onSelectApplicant={(applicant) => {
                    setSelectedApplicant(applicant);
                    setShowMobileDetail(true);

                    // Mark as read if it's unread
                    if (applicant.isUnread) {
                      dispatch(markPitchAsRead(applicant.id));
                    }
                  }}
                  showInviteMoreButton={activeTab === 'invited'}
                  browseURL={browseURL}
                />
              )}
            </Col>
            <Col
              md={8}
              xs={12}
              className={`${styles.detailsColumn} ${
                showMobileDetail ? styles.showMobile : styles.hiddenMobile
              }`}
            >
              {selectedApplicant ? (
                <>
                  {/* Mobile Back Button */}
                  <button
                    onClick={() => setShowMobileDetail(false)}
                    className={styles.mobileBackButton}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m12 19-7-7 7-7"></path>
                      <path d="M19 12H5"></path>
                    </svg>
                    <span>Back to list</span>
                  </button>

                  <ApplicantView
                    pitch={selectedPitch}
                    user={selectedApplicant.user}
                    applicantStage={selectedApplicant.stage}
                    onShortlist={handleShortlist}
                    onDecline={handleDecline}
                    onHire={handleHire}
                    onEndHire={handleEndHire}
                    onReset={handleReset}
                    decliningPitch={decliningPitch}
                    shortlistingPitch={shortlistingPitch}
                    endingHire={endingHire}
                    resettingPitch={resettingPitch}
                    hiringPitch={acceptingPitch}
                    currentUser={auth.user}
                    pitchMessages={pitchMessages}
                    pitchMessagesMeta={pitchMessagesMeta}
                    loadingPitchMessages={loadingPitchMessages}
                    markingThreadAsRead={markingThreadAsRead}
                    markedThreads={markedThreads}
                    onLoadMessages={handleLoadMessages}
                    onLoadMoreMessages={handleLoadMoreMessages}
                    onSendMessage={handleSendMessage}
                    onMarkThreadAsRead={handleMarkThreadAsRead}
                    briefEncodedId={match.params.id}
                  />
                </>
              ) : (
                <p className={styles.placeholder}>
                  Select an applicant to view their details
                </p>
              )}
            </Col>
          </>
        )}
      </Row>

      {/* Public Questions Modal */}
      <PublicQuestionsModal
        show={showQuestionsModal}
        onHide={() => setShowQuestionsModal(false)}
        questions={publicQuestions}
        briefId={brief?.id}
      />

      {/* Hire Modal with PaymentModal */}
      {selectedPitch && (
        <Modal
          closeButton
          show={showHireModal}
          title={`Hire ${
            selectedPitch.sender?.displayname || selectedPitch.sender?.username
          }`}
          onExit={handleCloseHireModal}
        >
          <PaymentModal
            brief={brief}
            pitch={selectedPitch}
            onAccept={handleAcceptPitchWithPayment}
            onAcceptPitchOverride={handleAcceptPitchWithoutPayment}
            closeModal={handleCloseHireModal}
            hireOthers={handleCloseHireModal}
          />
        </Modal>
      )}

      {/* Edit Job Modal */}
      <EditJobModal
        brief={brief}
        show={showEditModal}
        onClose={handleCloseEditModal}
        onSave={handleSaveJob}
        saving={savingBrief}
      />

      {/* Close Job Confirmation Modal */}
      <Modal
        closeButton
        show={showCloseJobModal}
        title="Close Job"
        onExit={handleCloseJobModalCancel}
      >
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '20px', fontSize: '16px' }}>
            Are you sure you want to close this job? This will stop new
            applications.
          </p>
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}
          >
            <ButtonWithLoader
              bsStyle="default"
              onClick={handleCloseJobModalCancel}
              disabled={closingJob}
              loading={false}
              text="Cancel"
            />
            <ButtonWithLoader
              bsStyle="danger"
              onClick={handleCloseJobConfirm}
              disabled={closingJob}
              loading={closingJob}
              text="Close Job"
            />
          </div>
        </div>
      </Modal>

      {/* Re-open Job Confirmation Modal */}
      <Modal
        closeButton
        show={showReopenJobModal}
        title="Re-open Job"
        onExit={handleReopenJobModalCancel}
      >
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '20px', fontSize: '16px' }}>
            Are you sure you want to re-open this job? This will allow new
            applicants to apply.
          </p>
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}
          >
            <ButtonWithLoader
              bsStyle="default"
              onClick={handleReopenJobModalCancel}
              disabled={reopeningJob}
              loading={false}
              text="Cancel"
            />
            <ButtonWithLoader
              bsStyle="primary"
              onClick={handleReopenJobConfirm}
              disabled={reopeningJob}
              loading={reopeningJob}
              text="Re-open Job"
            />
          </div>
        </div>
      </Modal>
    </Grid>
  );
};

JobDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default asyncConnect([
  {
    promise: ({
      store: { dispatch, getState },
      match: {
        params: { id },
      },
    }) => {
      const encodedId = id ? id.split('-')[0] : null;
      const state = getState();

      // Only load if not already in state
      if (encodedId && !state.clientATS?.brief) {
        return dispatch(loadBrief(encodedId));
      }

      return Promise.resolve();
    },
  },
])(JobDetails);
